import { VehicleInformation } from "@/components/booking/VehicleInformation";
import {
  formatCollectionDate,
  getJobCategoryBadge,
} from "@/components/booking/booking-card-utils";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { BookingResponseFooter } from "@/components/driver/BookingResponseFooter";
import { QuotePriceModal } from "@/components/driver/QuotePriceModal";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { TYRE_POSITIONS, getVehicleTypeLabel } from "@/data/vehicle";
import {
  syncBookingFromWebSocket,
  useBooking,
} from "@/hooks/queries/use-bookings-query";
import { useVehicle } from "@/hooks/queries/use-vehicles-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { showAlert } from "@/lib/alert";
import { queryKeys } from "@/lib/query-keys";
import { locationTrackingService } from "@/lib/services/location-tracking";
import { showToast } from "@/lib/toast";
import { getAtServiceCharge, getDriverOfferPrice } from "@/lib/utils";
import { formatETA } from "@/lib/utils/map-utils";
import { useAuthStore } from "@/stores/use-auth-store";
import { usePendingOffersStore } from "@/stores/use-pending-offers-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons";
import { useQueryClient } from "@tanstack/react-query";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import driverService from "../../lib/services/drivers";
import quoteService from "../../lib/services/quotes";
import { fieldLabels } from "../../lib/validation/booking";

export default function DriverBookingResponseScreen() {
  const router = useRouter();
  const desktop = useDesktopLayout();
  const qc = useQueryClient();
  const { user, setUser, theme } = useAuth();
  const { id, vehicleId, rejected } = useLocalSearchParams<{
    id: string;
    vehicleId: string;
    rejected: string;
  }>();
  const {
    data: booking,
    isLoading: bookingLoading,
    isError: bookingError,
    error: bookingLoadError,
  } = useBooking(Number(id));
  const {
    data: vehicle,
    isLoading: vehicleLoading,
    isError: vehicleError,
    error: vehicleLoadError,
  } = useVehicle(Number(vehicleId));
  const loading = bookingLoading || vehicleLoading;
  const rejectedBooking = rejected === "true";
  const [actionLoading, setActionLoading] = useState<
    "accept" | "reject" | "quote" | null
  >(null);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    vehicleId ? Number(vehicleId) : null,
  );
  const loadErrorShown = useRef(false);

  const estimatedPrice = useMemo(() => {
    if (!booking) {
      return null;
    }

    let estimated: number | null = null;
    if (booking.estimated_price != null) {
      estimated = Number(booking.estimated_price);
      if (isNaN(estimated)) {
        estimated = null;
      }
    }

    if (!estimated) {
      if (booking.pricing?.total_amount) {
        estimated = Number(booking.pricing.total_amount);
      } else if (booking.distance_miles) {
        estimated = 25 + Number(booking.distance_miles) * 2.5;
      } else {
        estimated = 50;
      }
    }

    return getDriverOfferPrice(
      estimated,
      user?.driver?.vat_status,
      booking.pricing?.operator_vat_amount,
    );
  }, [booking, user?.driver?.vat_status]);

  const platformFee = useMemo(
    () => getAtServiceCharge(booking?.pricing),
    [booking?.pricing],
  );

  const driverEarnings = useMemo(() => {
    if (estimatedPrice == null) {
      return null;
    }

    return Math.max(0, estimatedPrice - platformFee);
  }, [estimatedPrice, platformFee]);

  if (driverEarnings != null && quoteAmount === "") {
    setQuoteAmount(driverEarnings.toFixed(2));
  }

  useEffect(() => {
    if ((!bookingError && !vehicleError) || loadErrorShown.current) {
      return;
    }

    loadErrorShown.current = true;
    const loadError = bookingLoadError ?? vehicleLoadError;
    const message =
      loadError && typeof loadError === "object" && "response" in loadError
        ? (loadError as { response?: { data?: { message?: string } } }).response
            ?.data?.message
        : undefined;
    showAlert("Error", message || "Failed to load booking");
    router.back();
  }, [bookingError, vehicleError, bookingLoadError, vehicleLoadError, router]);

  const openQuoteModal = () => {
    if (driverEarnings != null) {
      setQuoteAmount(driverEarnings.toFixed(2));
    }
    setShowQuoteModal(true);
  };

  const handleQuickAccept = async () => {
    if (!vehicleId) {
      showAlert("Error", "Please select a vehicle first");
      return;
    }

    if (!estimatedPrice || typeof estimatedPrice !== "number") {
      showAlert("Error", "Estimated price not available");
      return;
    }

    const price = Number(estimatedPrice);
    const bookingId = Number(id);

    try {
      setActionLoading("accept");
      const { data: acceptedBooking } = await driverService.acceptBooking(
        bookingId,
        Number(vehicleId),
        price,
      );

      usePendingOffersStore.getState().removeOffer(bookingId);
      syncBookingFromWebSocket(qc, acceptedBooking);
      qc.invalidateQueries({ queryKey: queryKeys.bookings.scheduledJobs() });

      if (acceptedBooking?.status === "scheduled") {
        setActionLoading(null);
        showToast.success(
          "Scheduled job accepted. We'll remind you before it starts.",
        );
        router.replace(`/bookings/${id}`);
        return;
      }

      useAuthStore.getState().setActiveBooking(bookingId);

      setActionLoading(null);
      showToast.success("Booking accepted successfully");
      router.replace(`/bookings/${id}/live-driving`);

      void (async () => {
        try {
          if (user?.driver && !user.driver.is_online) {
            await locationTrackingService.setOnlineStatus(true);
            setUser({ ...user, driver: { ...user.driver, is_online: true } });
          }

          await locationTrackingService.startTracking(undefined, bookingId);
        } catch (trackingError) {
          console.error(
            "Failed to start location tracking after accept:",
            trackingError,
          );
        }
      })();
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to accept booking",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    showAlert(
      "Reject Booking",
      "Are you sure you want to reject this booking?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Reject",
          style: "destructive",
          onPress: async () => {
            try {
              setActionLoading("reject");
              await driverService.rejectBooking(Number(id));
              usePendingOffersStore.getState().removeOffer(Number(id));
              showToast.success("Booking rejected successfully");
              showAlert("Booking Rejected", "You have rejected this booking", [
                {
                  text: "OK",
                  onPress: () => {
                    router.back();
                  },
                },
              ]);
            } catch (error: any) {
              showToast.error(
                error.response?.data?.message || "Failed to reject booking",
              );
            } finally {
              setActionLoading(null);
            }
          },
        },
      ],
    );
  };

  const handleSubmitQuote = async () => {
    const quotedEarnings = parseFloat(quoteAmount);

    if (!quoteAmount || quotedEarnings <= 0) {
      showAlert("Error", "Please enter a valid earnings amount");
      return;
    }

    if (driverEarnings != null && quotedEarnings < driverEarnings) {
      showAlert(
        "Error",
        `Quote must be at least £${driverEarnings.toFixed(2)}`,
      );
      return;
    }

    if (!selectedVehicleId) {
      showAlert("Error", "Please select a vehicle first");
      return;
    }

    try {
      setActionLoading("quote");

      await quoteService.createQuote({
        booking_id: Number(id),
        vehicle_id: selectedVehicleId,
        amount: quotedEarnings + platformFee,
      });

      usePendingOffersStore.getState().removeOffer(Number(id));
      qc.invalidateQueries({ queryKey: queryKeys.bookings.scheduledJobs() });
      setShowQuoteModal(false);
      showToast.success("Quote submitted successfully");
      router.back();
    } catch (error: any) {
      showAlert(
        "Error",
        error.response?.data?.message || "Failed to submit quote",
      );
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <DesktopAppFrame>
        <Loader />
      </DesktopAppFrame>
    );
  }

  if (!booking || !vehicle) {
    return null;
  }

  const jobCategoryBadge = getJobCategoryBadge(
    booking.job_category,
    booking.scheduled_at,
  );

  return (
    <DesktopAppFrame>
      <SafeAreaView
        style={[
          styles.safeArea,
          {
            backgroundColor: theme.background,
            flexDirection: desktop ? "row" : "column",
          },
        ]}
        edges={desktop ? [] : ["bottom", "top"]}
      >
        <Stack.Screen options={{ title: "Booking Request" }} />
        <ScrollView
          style={[styles.container, { backgroundColor: theme.background }]}
          contentContainerStyle={styles.scrollContent}
        >
          <View style={styles.header}>
            <View style={styles.headerTitleRow}>
              <View style={styles.headerIcon}>
                <Ionicons
                  name={jobCategoryBadge?.icon ?? "information-circle"}
                  size={20}
                  color={BrandColors.white}
                />
              </View>
              <View style={styles.headerTextContainer}>
                <ThemedText style={styles.headerTitle} type="subtitle">
                  {jobCategoryBadge?.label ??
                    (rejectedBooking
                      ? "Booking Quote Rejected"
                      : "New Booking Request")}
                </ThemedText>
                <ThemedText style={styles.headerSubtitle} type="extraSmall">
                  {rejectedBooking
                    ? "You previously quoted for this booking was rejected. You can quote again."
                    : "Review the details and respond"}
                </ThemedText>
              </View>
            </View>
          </View>

          <View
            style={[
              styles.jobCategoryRow,
              {
                backgroundColor:
                  jobCategoryBadge?.backgroundColor ?? "#FEF3C7",
                borderLeftColor: jobCategoryBadge?.color ?? "#D97706",
                marginHorizontal: 16,
              },
            ]}
          >
            {jobCategoryBadge ? (
              <View style={styles.jobCategoryLabel}>
                <Ionicons
                  name="alert-circle-outline"
                  size={16}
                  color={jobCategoryBadge.color}
                />
                <ThemedText
                  style={[
                    styles.jobCategoryText,
                    { color: jobCategoryBadge.color },
                  ]}
                  type="extraSmall"
                >
                  {jobCategoryBadge.description}
                </ThemedText>
              </View>
            ) : null}
          </View>

          <View
            style={[
              styles.card,
              { backgroundColor: theme.card, borderColor: theme.border },
            ]}
          >
            <ThemedText type="smallSemiBold" style={styles.cardTitle}>
              Booking Details
            </ThemedText>

            <ThemedText type="smallSemiBold" style={{ marginBottom: 8, fontSize: 12 }}>
              {booking.service_type_label}
            </ThemedText>

            <View style={styles.detailsInner}>
              <View style={styles.routeSection}>
                <View style={styles.routeIconColumn}>
                  <View style={styles.pickupIconOuter}>
                    <View style={styles.pickupIconInner} />
                  </View>
                  {booking.to_address || booking.to_area ? (
                    <View style={styles.routeDashedLine}>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <View key={index} style={styles.routeDash} />
                      ))}
                    </View>
                  ) : null}
                  {booking.to_address || booking.to_area ? (
                    <Ionicons name="location" size={18} color="#E11D48" />
                  ) : null}
                </View>

                <View style={styles.routeDetails}>
                  <View style={styles.routeStop}>
                    <ThemedText style={styles.pickupLabel} type="extraSmall">
                      Pick Up
                    </ThemedText>
                    <ThemedText
                      style={styles.routeAddress}
                      type="smallSemiBold"
                      numberOfLines={3}
                    >
                      {booking.from_address ??
                        booking.from_area ??
                        "Area withheld until assignment"}
                    </ThemedText>
                  </View>

                  {booking.to_address || booking.to_area ? (
                    <View style={styles.routeStop}>
                      <ThemedText
                        style={styles.destinationLabel}
                        type="extraSmall"
                      >
                        Destination
                      </ThemedText>
                      <ThemedText
                        style={styles.routeAddress}
                        type="smallSemiBold"
                        numberOfLines={3}
                      >
                        {booking.to_address ?? booking.to_area}
                      </ThemedText>
                    </View>
                  ) : null}
                </View>
              </View>

              {booking.distance_miles ? (
                <View style={styles.scheduleRow}>
                  <View style={styles.scheduleIconWrap}>
                    <MaterialCommunityIcons
                      name="map-marker-distance"
                      size={18}
                      color={BrandColors.primary}
                    />
                  </View>
                  <View style={styles.scheduleContent}>
                    <ThemedText style={styles.pickupLabel} type="extraSmall">
                      Distance & Duration
                    </ThemedText>
                    <ThemedText
                      style={styles.routeAddress}
                      type="smallSemiBold"
                    >
                      {Number(booking.distance_miles).toFixed()} miles •{" "}
                      {formatETA(booking.estimated_duration_minutes)}
                    </ThemedText>
                  </View>
                </View>
              ) : null}

              {booking.scheduled_at ? (
                <View style={styles.scheduleRow}>
                  <View style={styles.scheduleIconWrap}>
                    <Ionicons
                      name="calendar-outline"
                      size={16}
                      color={BrandColors.primary}
                    />
                  </View>
                  <View style={styles.scheduleContent}>
                    <ThemedText style={styles.detailLabel} type="extraSmall">
                      Collection Date & Time
                    </ThemedText>
                    <ThemedText style={styles.detailValue} type="smallSemiBold">
                      {formatCollectionDate(booking.scheduled_at)}
                    </ThemedText>
                  </View>
                </View>
              ) : null}

              <View style={styles.respondWarning}>
                <Ionicons name="time-outline" size={16} color="#D97706" />
                <ThemedText
                  style={styles.respondWarningText}
                  type="extraSmallSemiBold"
                >
                  Please respond within 15 Seconds
                </ThemedText>
              </View>
            </View>
          </View>

          <View style={[{ padding: 0, paddingVertical: 14 }]}>
            <VehicleInformation booking={booking} variant="list" />
          </View>

          {/* Vehicle Collection & Pickup Information */}
          <View
            style={[
              styles.card,
              { backgroundColor: theme.card, borderColor: theme.border },
            ]}
          >
            <ThemedText type="smallSemiBold" style={styles.cardTitle}>
            Customer responses
            </ThemedText>

            {/* Dynamic Questions from booking.questions */}
            {booking.questions &&
              booking.questions.length > 0 &&
              Object.entries(booking.questions[0]).map(([key, value]) => {
                if (
                  value === undefined ||
                  value === null ||
                  value === "" ||
                  key === "notes" ||
                  key === "special_issues"
                )
                  return null;
                if (fieldLabels[key]) {
                  return (
                    <View
                      key={key}
                      style={[
                        styles.infoRow,
                        { borderTopColor: theme.border },
                      ]}
                    >
                      <ThemedText
                        style={styles.infoQuestion}
                        type="extraSmall"
                        lightColor={BrandColors.darkGray}
                        darkColor={BrandColors.mediumGray}
                      >
                        {fieldLabels[key]
                          .replace("Please indicate whether", "")
                          .replace("Please select", "")
                          .replace("Please confirm", "")
                          .replace(/\?$/, "")
                          .trim()
                          .replace(/^./, (char) => char.toUpperCase())}
                      </ThemedText>
                      <ThemedText style={styles.infoAnswer} type="extraSmall">
                        {typeof value === "boolean"
                          ? value
                            ? "Yes"
                            : "No"
                          : key === "tyre_positions"
                            ? String(value)
                                .split(",")
                                .map(
                                  (v) =>
                                    TYRE_POSITIONS.find((t) => t.value === v)
                                      ?.label,
                                )
                                .join(", ")
                            : String(value)}
                      </ThemedText>
                    </View>
                  );
                }
                return null;
              })}

            {/* Passenger Count */}
            {booking?.passenger_count !== undefined &&
              booking.passenger_count > 0 && (
                <View
                  style={[styles.infoRow, { borderTopColor: theme.border }]}
                >
                  <ThemedText
                    style={styles.infoQuestion}
                    type="extraSmall"
                    lightColor={BrandColors.darkGray}
                    darkColor={BrandColors.mediumGray}
                  >
                    Number of passengers
                  </ThemedText>
                  <ThemedText style={styles.infoAnswer} type="extraSmall">
                    {booking.passenger_count}
                  </ThemedText>
                </View>
              )}

            {/* Pet Count */}
            {booking?.pet_count !== undefined && booking.pet_count > 0 && (
              <View style={[styles.infoRow, { borderTopColor: theme.border }]}>
                <ThemedText
                  style={styles.infoQuestion}
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.mediumGray}
                >
                  Number of pets
                </ThemedText>
                <ThemedText style={styles.infoAnswer} type="extraSmall">
                  {booking.pet_count}
                </ThemedText>
              </View>
            )}

            {/* inspection required */}
            {booking.questions && booking.questions[0]?.notes && (
              <View style={[styles.infoRow, { borderTopColor: theme.border }]}>
                <ThemedText
                  style={styles.infoQuestion}
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.mediumGray}
                >
                  Inspection required
                </ThemedText>
                <ThemedText style={styles.infoAnswer} type="extraSmall">
                  {booking.inspection_required ? "Yes" : "No"}
                </ThemedText>
              </View>
            )}

            {/* Question Notes */}
            {booking.questions && booking.questions[0]?.notes && (
              <View
                style={[
                  styles.infoRow,
                  styles.infoRowStacked,
                  { borderTopColor: theme.border },
                ]}
              >
                <ThemedText
                  type="extraSmall"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.mediumGray}
                >
                  Additional collection notes
                </ThemedText>
                <ThemedText type="extraSmall">
                  {booking.questions[0].notes}
                </ThemedText>
              </View>
            )}
          </View>

          {/* Vehicle Selection */}
          {vehicle && (
            <View
              style={[
                styles.card,
                { backgroundColor: theme.card, borderColor: theme.border },
              ]}
            >
              <ThemedText type="smallSemiBold" style={styles.cardTitle}>
                Select Vehicle
              </ThemedText>
              <TouchableOpacity
                style={[
                  styles.vehicleOption,
                  selectedVehicleId === vehicle.id &&
                    styles.vehicleOptionActive,
                ]}
                onPress={() => setSelectedVehicleId(vehicle.id)}
                activeOpacity={0.8}
                accessibilityLabel="select-vehicle-button"
                accessibilityRole="button"
                accessibilityHint="Select a vehicle for the booking"
              >
                <View style={styles.vehicleOptionContent}>
                  <View style={styles.vehicleInfo}>
                    <ThemedText
                      style={[
                        styles.vehicleRegistration,
                        selectedVehicleId === vehicle.id &&
                          styles.vehicleRegistrationActive,
                      ]}
                    >
                      {[vehicle.make, vehicle.model]
                        .filter(Boolean)
                        .join(" ") || vehicle.registration_number}
                    </ThemedText>
                    <ThemedText style={styles.vehicleType}>
                      {getVehicleTypeLabel(vehicle.vehicle_type)}
                    </ThemedText>
                  </View>
                  <View style={[styles.radioOuter, styles.radioOuterSelected]}>
                    {selectedVehicleId === vehicle.id ? (
                      <View style={styles.radioInner} />
                    ) : null}
                  </View>
                </View>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>

        {driverEarnings != null ? (
          <View style={desktop ? styles.desktopOffer : undefined}>
            <BookingResponseFooter
              driverEarnings={driverEarnings}
              loading={actionLoading}
              disabled={!selectedVehicleId}
              canQuoteHigher={booking?.customer?.can_quote === true}
              onQuoteHigher={openQuoteModal}
              onDecline={handleReject}
              onAccept={handleQuickAccept}
            />
          </View>
        ) : null}

        {driverEarnings != null ? (
          <QuotePriceModal
            visible={showQuoteModal}
            minimumEarnings={driverEarnings}
            quoteEarnings={quoteAmount}
            loading={actionLoading === "quote"}
            onClose={() => setShowQuoteModal(false)}
            onChangeEarnings={setQuoteAmount}
            onSubmit={handleSubmitQuote}
          />
        ) : null}
      </SafeAreaView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  desktopOffer: {
    width: 380,
    maxWidth: "40%",
    paddingTop: 24,
    paddingRight: 24,
  },
  replyBar: {
    borderTopWidth: 1,
    padding: 16,
  },
  replyButton: {
    backgroundColor: BrandColors.primary,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
  },
  replyButtonText: {
    color: "#fff",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 20,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    lineHeight: 26,
  },
  headerSubtitle: {
    color: BrandColors.mediumGray,
    marginTop: 2,
  },
  card: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  cardTitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  jobCategoryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderLeftWidth: 3,
    paddingHorizontal: 12,
    paddingVertical: 14,
    marginBottom: 16,
  },
  jobCategoryLabel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
  },
  jobCategoryText: {
    flexShrink: 1,
  },
  respondWithinText: {
    flexShrink: 1,
    textAlign: "right",
    marginLeft: "auto",
  },
  responsesTitle: {
    marginBottom: 8,
  },
  detailsInner: {
    gap: 16,
  },
  routeSection: {
    flexDirection: "row",
    gap: 12,
  },
  routeIconColumn: {
    alignItems: "center",
    paddingTop: 2,
  },
  pickupIconOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  pickupIconInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.white,
  },
  routeDashedLine: {
    alignItems: "center",
    gap: 3,
    marginVertical: 6,
  },
  routeDash: {
    width: 2,
    height: 5,
    borderRadius: 1,
    backgroundColor: BrandColors.lightGray,
  },
  routeDetails: {
    flex: 1,
    gap: 16,
  },
  routeStop: {
    gap: 4,
  },
  pickupLabel: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  destinationLabel: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  routeAddress: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 19,
  },
  scheduleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  scheduleIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BrandColors.primaryLight,
    alignItems: "center",
    justifyContent: "center",
  },
  scheduleContent: {
    flex: 1,
    gap: 4,
  },
  detailLabel: {
    fontSize: 12,
    color: BrandColors.mediumGray,
  },
  detailValue: {
    fontSize: 15,
    fontWeight: "600",
  },
  respondWarning: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#FEF3C7",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  respondWarningText: {
    color: "#92400E",
    flex: 1,
  },
  vehicleOption: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: BrandColors.lightGray,
    marginBottom: 12,
    backgroundColor: BrandColors.white,
  },
  vehicleOptionActive: {
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.primaryLight,
  },
  vehicleOptionContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  vehicleInfo: {
    flex: 1,
  },
  vehicleRegistration: {
    fontSize: 16,
    fontWeight: "700",
    color: BrandColors.charcoal,
    textTransform: "uppercase",
  },
  vehicleRegistrationActive: {
    color: BrandColors.primary,
  },
  vehicleType: {
    fontSize: 14,
    color: BrandColors.mediumGray,
    marginTop: 4,
  },
  radioOuter: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: BrandColors.lightGray,
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuterSelected: {
    borderColor: BrandColors.primary,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: BrandColors.primary,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 16,
  },
  infoRowStacked: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
  },
  warningRow: {
    backgroundColor: "#FFF7ED",
    paddingHorizontal: 12,
    borderRadius: 8,
    borderBottomWidth: 0,
    marginTop: 4,
  },
  infoQuestion: {
    flex: 1,
  },
  infoAnswer: {
    maxWidth: "50%",
    textAlign: "right",
    textTransform: "capitalize",
  },
});
